import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Bot, AlertTriangle, ChevronDown, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const FEEDBACK_TYPES = [
  { value: "too_confident", label: "Too confident/absolute" },
  { value: "too_legal_advice", label: "Sounds like legal advice" },
  { value: "too_speculative", label: "Too speculative" },
  { value: "wrong_terminology", label: "Wrong terminology" },
  { value: "hallucination", label: "Contains hallucination" },
  { value: "good_content", label: "Good content (positive)" },
];

const SEVERITY_COLORS = {
  high: "bg-red-100 text-red-800 border-red-200",
  medium: "bg-yellow-100 text-yellow-800 border-yellow-200",
  low: "bg-blue-100 text-blue-800 border-blue-200",
};

export default function AIMetadataPanel({ metadata, postId, onFeedbackSubmit }) {
  const [feedbackType, setFeedbackType] = useState("");
  const [feedbackDetails, setFeedbackDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showRiskFlags, setShowRiskFlags] = useState(false);

  const handleSubmitFeedback = async () => {
    if (!feedbackType) {
      toast.error("Please select a feedback type");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setSubmitting(true);

    try {
      await axios.post(
        `${API}/admin/blog/ai/feedback`,
        {
          post_id: postId,
          feedback_type: feedbackType,
          details: feedbackDetails || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      toast.success("Feedback submitted - guidance will be updated");
      setFeedbackType("");
      setFeedbackDetails("");
      if (onFeedbackSubmit) onFeedbackSubmit();
    } catch (error) {
      toast.error("Failed to submit feedback");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString();
  };

  const riskFlags = metadata?.risk_flags || [];
  const highSeverityCount = riskFlags.filter(f => f.severity === "high").length;
  const mediumSeverityCount = riskFlags.filter(f => f.severity === "medium").length;

  return (
    <div className="border border-blue-200 bg-blue-50/50 rounded-lg p-4 space-y-4" data-testid="ai-metadata-panel">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Bot className="w-5 h-5 text-blue-600" />
        <h3 className="font-medium text-blue-900">AI Generated Content</h3>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div>
          <span className="text-muted-foreground">Prompt Version:</span>
          <span className="ml-2 font-mono">{metadata?.prompt_version || "?"}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Model:</span>
          <span className="ml-2">{metadata?.model_used || "?"}</span>
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Generated:</span>
          <span className="ml-2">{formatDate(metadata?.generated_at)}</span>
        </div>
      </div>

      {/* Risk Flags Summary */}
      {riskFlags.length > 0 && (
        <Collapsible open={showRiskFlags} onOpenChange={setShowRiskFlags}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500" />
                {riskFlags.length} Content Flags
                {highSeverityCount > 0 && (
                  <Badge variant="destructive" className="text-xs">{highSeverityCount} High</Badge>
                )}
                {mediumSeverityCount > 0 && (
                  <Badge className="text-xs bg-amber-500">{mediumSeverityCount} Medium</Badge>
                )}
              </span>
              <ChevronDown className={`w-4 h-4 transition-transform ${showRiskFlags ? "rotate-180" : ""}`} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="mt-2 space-y-2">
            {riskFlags.map((flag, idx) => (
              <div 
                key={idx} 
                className={`text-xs p-2 rounded border ${SEVERITY_COLORS[flag.severity] || SEVERITY_COLORS.low}`}
              >
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium capitalize">{flag.flag_type.replace(/_/g, " ")}</span>
                  <Badge variant="outline" className="text-xs">{flag.severity}</Badge>
                </div>
                <div className="font-mono bg-white/50 p-1 rounded mt-1">
                  "{flag.text_snippet}"
                </div>
              </div>
            ))}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Previous Feedback */}
      {metadata?.feedback?.length > 0 && (
        <div className="text-sm">
          <span className="text-muted-foreground">Previous feedback:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {metadata.feedback.map((fb, idx) => (
              <Badge key={idx} variant="secondary" className="text-xs">
                {fb.replace(/_/g, " ")}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Feedback Form */}
      <div className="border-t pt-4 space-y-3">
        <h4 className="text-sm font-medium">Submit Feedback</h4>
        <Select value={feedbackType} onValueChange={setFeedbackType}>
          <SelectTrigger data-testid="feedback-type-select">
            <SelectValue placeholder="Select feedback type" />
          </SelectTrigger>
          <SelectContent>
            {FEEDBACK_TYPES.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Textarea
          placeholder="Additional details (optional)"
          value={feedbackDetails}
          onChange={(e) => setFeedbackDetails(e.target.value)}
          rows={2}
          className="text-sm"
          data-testid="feedback-details"
        />
        
        <Button 
          onClick={handleSubmitFeedback} 
          disabled={submitting || !feedbackType}
          size="sm"
          className="w-full"
          data-testid="submit-feedback-btn"
        >
          <Send className="w-4 h-4 mr-2" />
          {submitting ? "Submitting..." : "Submit Feedback"}
        </Button>
      </div>
    </div>
  );
}
